import React, { useEffect, useState } from 'react';
import {
    Modal,
    Tabs,
    Table,
    Button,
    Input,
    InputNumber,
    Select,
    Tag,
    Space,
    Popconfirm,
    Card,
    Typography,
    Divider,
    message,
    Tooltip,
    Badge,
    Row,
    Col
} from 'antd';
import {
    ShoppingOutlined,
    PlusOutlined,
    DeleteOutlined,
    TrophyOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SearchOutlined,
    ReloadOutlined,
    HistoryOutlined,
    DollarCircleOutlined
} from '@ant-design/icons';
import { GetJutgeUsuariIdPoints } from '../../../dao/jutge/GetJutgeUsuariIdPoints';
import { GetJutgeUsuariIdPointsHistory } from '../../../dao/jutge/GetJutgeUsuariIdPointsHistory';
import { GetJutgeUsuariIdJavaballLogs } from '../../../dao/jutge/GetJutgeUsuariIdJavaballLogs';
import { GetJutgeUsuariIdJavamons } from '../../../dao/jutge/GetJutgeUsuariIdJavamons';
import { GetShopItems } from '../../../dao/jutge/GetShopItems';
import { GetJavamons } from '../../../dao/jutge/GetJavamons';
import { DeleteJutgeUsuariIdPointsId } from '../../../dao/jutge/DeleteJutgeUsuariIdPointsId';
import {
    AssignShopItemToUser,
    RemoveShopUserItem,
    AssignJavamonToUser,
    RemoveJavamonFromUser,
    AdjustUserPoints
} from '../../../dao/jutge/UserInventoryAdminDAO';
import { getJavamonImage as getExternalJavamonImage } from '../../../dao/jutge/GetJavamonConfig';

const { Text, Title } = Typography;

interface UserInventoryModalProps {
    visible: boolean;
    onClose: (updatedPoints?: { pointsTotal: number; pointsCurrent: number }) => void;
    user: any;
    aulaId?: string | number;
}

export const UserInventoryModal: React.FC<UserInventoryModalProps> = ({
    visible,
    onClose,
    user,
    aulaId = "0"
}) => {
    const userId = user?.id_usuari != null ? user.id_usuari : user?.id;

    const [activeTab, setActiveTab] = useState<string>('items');
    const [loading, setLoading] = useState(false);

    // Datos del usuario
    const [pointsEntries, setPointsEntries] = useState<any[]>([]);
    const [pointsHistoryEntries, setPointsHistoryEntries] = useState<any[]>([]);
    const [javaballLogs, setJavaballLogs] = useState<any[]>([]);
    const [userJavamons, setUserJavamons] = useState<any[]>([]);

    // Catálogos globales
    const [shopCatalog, setShopCatalog] = useState<any[]>([]);
    const [javamonCatalog, setJavamonCatalog] = useState<any[]>([]);

    // Estados para asignación rápida
    const [selectedShopItem, setSelectedShopItem] = useState<number | undefined>(undefined);
    const [selectedJavamon, setSelectedJavamon] = useState<number | undefined>(undefined);

    // Estados para ajuste de puntos
    const [pointsDelta, setPointsDelta] = useState<number>(10);
    const [pointsReason, setPointsReason] = useState<string>('');

    // Búsquedas internas en tablas
    const [searchItem, setSearchItem] = useState('');
    const [searchJavamon, setSearchJavamon] = useState('');

    const fetchData = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const [pts, historyPts, jbLogs, jms, shop, allJms] = await Promise.all([
                GetJutgeUsuariIdPoints(String(userId)),
                GetJutgeUsuariIdPointsHistory(String(userId)),
                GetJutgeUsuariIdJavaballLogs(String(userId)),
                GetJutgeUsuariIdJavamons(String(userId)),
                GetShopItems(String(aulaId || "0")),
                GetJavamons()
            ]);

            setPointsEntries(Array.isArray(pts) ? pts : []);
            setPointsHistoryEntries(Array.isArray(historyPts) ? historyPts : []);
            setJavaballLogs(Array.isArray(jbLogs) ? jbLogs : []);
            setUserJavamons(Array.isArray(jms) ? jms : []);
            setShopCatalog(Array.isArray(shop) ? shop : []);
            setJavamonCatalog(Array.isArray(allJms) ? allJms : []);
        } catch (e) {
            console.error(e);
            message.error("Error al carregar les dades de l'alumne");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && userId) {
            fetchData();
        }
    }, [visible, userId, aulaId]);

    // Separación de puntos, items comprados y gasto de puntos
    const userShopItems = pointsEntries.filter((p: any) => p.id_item != null);
    
    // Si tenemos el historial completo (incluyendo penalizaciones y compras), lo usamos como base
    const sourceEntries = pointsHistoryEntries.length > 0 ? pointsHistoryEntries : pointsEntries;

    // Puntos obtenidos (recompensas/positivos)
    const userPointsHistory = sourceEntries.filter((p: any) => p.id_item == null && p.ball !== 'JAVABALL_LOG' && Number(p.punts) > 0);
    // Registros de gasto, penalizaciones o lanzamientos de Javaball
    const userSpentPointsHistory = sourceEntries.filter((p: any) => {
        const val = Number(p.punts) || 0;
        const tipo = (p.tipo || '').toLowerCase();
        const desc = (p.descripcio || '').toLowerCase();
        return val < 0 || p.ball === 'JAVABALL_LOG' || tipo.includes('gasto') || tipo.includes('lanzamiento') || desc.includes('compra') || desc.includes('penalitzaci');
    });
    const totalPunts = userPointsHistory.reduce((acc, curr) => acc + (Number(curr.punts) || 0), 0);
    const totalGasto = userSpentPointsHistory
        .filter((p: any) => p.ball !== 'JAVABALL_LOG')
        .reduce((acc, curr) => acc + Math.abs(Number(curr.punts) || 0), 0);
    const puntsActuals = totalPunts - totalGasto;

    // Handlers Items Tienda
    const handleAssignItem = async () => {
        if (!selectedShopItem) {
            message.warning("Selecciona un item de la botiga");
            return;
        }
        setLoading(true);
        const ok = await AssignShopItemToUser(userId, selectedShopItem);
        if (ok) {
            message.success("Item afegit correctament a l'inventari");
            setSelectedShopItem(undefined);
            const pts = await GetJutgeUsuariIdPoints(String(userId));
            setPointsEntries(Array.isArray(pts) ? pts : []);
        } else {
            message.error("Error en afegir l'item");
        }
        setLoading(false);
    };

    const handleRemoveItem = async (instanceId: number) => {
        setLoading(true);
        const ok = await RemoveShopUserItem(instanceId, userId);
        if (ok) {
            message.success("Item consumit / eliminat");
            const pts = await GetJutgeUsuariIdPoints(String(userId));
            setPointsEntries(Array.isArray(pts) ? pts : []);
        } else {
            message.error("Error en eliminar l'item");
        }
        setLoading(false);
    };

    // Handlers Javamons
    const handleAssignJavamon = async () => {
        if (!selectedJavamon) {
            message.warning("Selecciona un Javamon");
            return;
        }
        setLoading(true);
        const ok = await AssignJavamonToUser(userId, selectedJavamon);
        if (ok) {
            message.success("Javamon assignat a l'alumne");
            setSelectedJavamon(undefined);
            const jms = await GetJutgeUsuariIdJavamons(String(userId));
            setUserJavamons(Array.isArray(jms) ? jms : []);
        } else {
            message.error("Error en assignar el Javamon");
        }
        setLoading(false);
    };

    const handleRemoveJavamon = async (instanceId: number) => {
        setLoading(true);
        const ok = await RemoveJavamonFromUser(userId, instanceId);
        if (ok) {
            message.success("Javamon retirat de l'equip de l'alumne");
            const jms = await GetJutgeUsuariIdJavamons(String(userId));
            setUserJavamons(Array.isArray(jms) ? jms : []);
        } else {
            message.error("Error en retirar el Javamon");
        }
        setLoading(false);
    };

    // Handlers Puntos / Recompensas
    const handleAdjustPoints = async (isPenalty: boolean = false) => {
        const delta = isPenalty ? -Math.abs(pointsDelta) : Math.abs(pointsDelta);
        if (!delta) {
            message.warning("Introdueix una quantitat de punts");
            return;
        }
        const reason = pointsReason.trim() || (isPenalty ? "Penalització docent" : "Recompensa docent");
        const tipo = isPenalty ? "Penalització" : "Recompensa";

        setLoading(true);
        const ok = await AdjustUserPoints(userId, delta, reason, tipo);
        if (ok) {
            message.success(isPenalty ? `Penalització de ${delta} punts aplicada` : `Recompensa de +${delta} punts atorgada`);
            setPointsReason('');
            const [pts, hist, jbLogs] = await Promise.all([
                GetJutgeUsuariIdPoints(String(userId)),
                GetJutgeUsuariIdPointsHistory(String(userId)),
                GetJutgeUsuariIdJavaballLogs(String(userId))
            ]);
            setPointsEntries(Array.isArray(pts) ? pts : []);
            setPointsHistoryEntries(Array.isArray(hist) ? hist : []);
            setJavaballLogs(Array.isArray(jbLogs) ? jbLogs : []);
        } else {
            message.error("Error en ajustar els punts");
        }
        setLoading(false);
    };

    const handleDeletePointRecord = async (pointId: number) => {
        setLoading(true);
        try {
            await DeleteJutgeUsuariIdPointsId(String(userId), String(pointId), null);
            message.success("Registre de punts eliminat");
            const [pts, hist, jbLogs] = await Promise.all([
                GetJutgeUsuariIdPoints(String(userId)),
                GetJutgeUsuariIdPointsHistory(String(userId)),
                GetJutgeUsuariIdJavaballLogs(String(userId))
            ]);
            setPointsEntries(Array.isArray(pts) ? pts : []);
            setPointsHistoryEntries(Array.isArray(hist) ? hist : []);
            setJavaballLogs(Array.isArray(jbLogs) ? jbLogs : []);
        } catch (e) {
            message.error("Error en eliminar el registre");
        } finally {
            setLoading(false);
        }
    };

    const filteredShopItems = userShopItems.filter((it: any) =>
        (it.tipo || '').toLowerCase().includes(searchItem.toLowerCase()) ||
        (it.descripcio || '').toLowerCase().includes(searchItem.toLowerCase())
    );

    const filteredUserJavamons = userJavamons.filter((jm: any) =>
        (jm.nom || '').toLowerCase().includes(searchJavamon.toLowerCase()) ||
        (jm.nom_origin || '').toLowerCase().includes(searchJavamon.toLowerCase()) ||
        (jm.descr || '').toLowerCase().includes(searchJavamon.toLowerCase())
    );

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🎒</span>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>
                            Gestió d'Inventari i Recompenses
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: '#8c8c8c' }}>
                            Alumne: <Text strong>{user?.nom} {user?.cognoms}</Text> ({user?.email || `ID: ${userId}`})
                        </div>
                    </div>
                </div>
            }
            open={visible}
            onCancel={() => onClose({ pointsTotal: totalPunts, pointsCurrent: puntsActuals })}
            width={880}
            footer={[
                <Button key="close" type="primary" onClick={() => onClose({ pointsTotal: totalPunts, pointsCurrent: puntsActuals })}>
                    Tancar
                </Button>
            ]}
            destroyOnClose
        >
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', padding: '10px 16px', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                <Space size={16}>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Items botiga:</Text>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#1890ff' }}>{userShopItems.length}</div>
                    </div>
                    <Divider type="vertical" style={{ height: 32 }} />
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Javamons:</Text>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#722ed1' }}>{userJavamons.length}</div>
                    </div>
                    <Divider type="vertical" style={{ height: 32 }} />
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Punts totals:</Text>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#1890ff' }}>
                            {totalPunts} pts
                        </div>
                    </div>
                    <Divider type="vertical" style={{ height: 32 }} />
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Total gastat:</Text>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#fa541c' }}>
                            -{totalGasto} pts
                        </div>
                    </div>
                    <Divider type="vertical" style={{ height: 32 }} />
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Punts actuals:</Text>
                        <div style={{ fontWeight: 700, fontSize: 16, color: puntsActuals >= 0 ? '#52c41a' : '#f5222d' }}>
                            {puntsActuals} pts
                        </div>
                    </div>
                </Space>
                <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} size="small">
                    Refrescar
                </Button>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'items',
                        label: <span><ShoppingOutlined /> Items Botiga ({userShopItems.length})</span>,
                        children: (
                            <div>
                                {/* Asignador rápido */}
                                <Card size="small" style={{ marginBottom: 16, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                    <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: 8 }}>
                                        + Asignar nou Item de la Botiga
                                    </div>
                                    <Space wrap>
                                        <Select
                                            showSearch
                                            placeholder="Seleccionar item de la botiga..."
                                            style={{ width: 340 }}
                                            value={selectedShopItem}
                                            onChange={setSelectedShopItem}
                                            filterOption={(input, option: any) =>
                                                (option?.children || '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {shopCatalog
                                                .filter((item: any) => {
                                                    const rawType = (item.type || '').trim().toLowerCase();
                                                    // Tipos explícitamente PROHIBIDOS: equipamiento, skins, avatares, javamons
                                                    const forbiddenTypes = [
                                                        'avatar', 'skin', 'weapon', 'arma',
                                                        'bow', 'arc', 'arrow', 'flecha',
                                                        'magic', 'magia', 'javamon', 'shield', 'escut'
                                                    ];
                                                    if (forbiddenTypes.includes(rawType)) {
                                                        return false;
                                                    }
                                                    // Si tiene id_game_item (apunta a una tabla externa de skin/arma/etc.) no es un consumible o item directo de tienda
                                                    if (item.id_game_item != null && item.id_game_item !== 0 && item.id_game_item !== '') {
                                                        return false;
                                                    }
                                                    // Tipos PERMITIDOS: Consumibles, Javaball y Habilidad
                                                    const allowedTypes = ['consumable', 'consumible', 'javaball', 'ability', 'habilidad'];
                                                    return allowedTypes.includes(rawType);
                                                })
                                                .map((item: any) => (
                                                    <Select.Option key={item.id} value={item.id}>
                                                        {item.nom} ({item.type || 'Consumible'} - {item.cost ?? 0} pts)
                                                    </Select.Option>
                                                ))}
                                        </Select>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleAssignItem}
                                            loading={loading}
                                            disabled={!selectedShopItem}
                                        >
                                            Afegir Item
                                        </Button>
                                    </Space>
                                </Card>

                                {/* Listado de items del alumno */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Input
                                        placeholder="Cercar item a l'inventari..."
                                        prefix={<SearchOutlined />}
                                        value={searchItem}
                                        onChange={(e) => setSearchItem(e.target.value)}
                                        style={{ width: 280 }}
                                        allowClear
                                    />
                                </div>

                                <Table
                                    dataSource={filteredShopItems}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    columns={[
                                        {
                                            title: 'Icona',
                                            key: 'img',
                                            width: 60,
                                            render: (_, r: any) => {
                                                const imgUrl = getExternalJavamonImage(r);
                                                return imgUrl ? (
                                                    <img
                                                        src={imgUrl}
                                                        alt={r.tipo}
                                                        style={{ width: 36, height: 36, objectFit: 'contain' }}
                                                        onError={(e) => {
                                                            (e.target as HTMLElement).style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <span style={{ fontSize: 22 }}>📦</span>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Nom / Tipus',
                                            dataIndex: 'tipo',
                                            key: 'tipo',
                                            render: (val, r: any) => (
                                                <div>
                                                    <Text strong>{val}</Text>
                                                    {r.ball && <Tag color="blue" style={{ marginLeft: 8 }}>{r.ball}</Tag>}
                                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.descripcio}</div>
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'Quantitat',
                                            key: 'quantity',
                                            dataIndex: 'quantity',
                                            align: 'center',
                                            width: 90,
                                            render: (val) => (
                                                <Badge
                                                    count={val != null && val !== undefined ? Number(val) : 1}
                                                    overflowCount={999}
                                                    style={{ backgroundColor: '#108ee9', fontSize: 13, fontWeight: 700 }}
                                                />
                                            )
                                        },
                                        {
                                            title: 'Accions',
                                            key: 'actions',
                                            align: 'center',
                                            width: 100,
                                            render: (_, r: any) => (
                                                <Popconfirm
                                                    title="Eliminar / Consumir item?"
                                                    description="Aquesta acció retirarà 1 unitat d'aquest item de l'alumne."
                                                    onConfirm={() => handleRemoveItem(r.id)}
                                                    okText="Eliminar"
                                                    cancelText="Cancel·lar"
                                                    okButtonProps={{ danger: true }}
                                                >
                                                    <Button danger size="small" icon={<DeleteOutlined />}>
                                                        Treure
                                                    </Button>
                                                </Popconfirm>
                                            )
                                        }
                                    ]}
                                />
                            </div>
                        )
                    },
                    {
                        key: 'javamons',
                        label: <span>🐾 Javamons ({userJavamons.length})</span>,
                        children: (
                            <div>
                                {/* Asignador rápido de Javamon */}
                                <Card size="small" style={{ marginBottom: 16, background: '#f9f0ff', border: '1px solid #d3adf7' }}>
                                    <div style={{ fontWeight: 600, color: '#722ed1', marginBottom: 8 }}>
                                        + Asignar nou Javamon
                                    </div>
                                    <Space wrap>
                                        <Select
                                            showSearch
                                            placeholder="Seleccionar Javamon del catàleg..."
                                            style={{ width: 340 }}
                                            value={selectedJavamon}
                                            onChange={setSelectedJavamon}
                                            filterOption={(input, option: any) =>
                                                (option?.children || '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {javamonCatalog.map((jm: any) => (
                                                <Select.Option key={jm.id} value={jm.id}>
                                                    #{jm.id} {jm.nom} ({jm.rarity || 'Normal'})
                                                </Select.Option>
                                            ))}
                                        </Select>
                                        <Button
                                            type="primary"
                                            style={{ background: '#722ed1', borderColor: '#722ed1' }}
                                            icon={<PlusOutlined />}
                                            onClick={handleAssignJavamon}
                                            loading={loading}
                                            disabled={!selectedJavamon}
                                        >
                                            Atorgar Javamon
                                        </Button>
                                    </Space>
                                </Card>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <Input
                                        placeholder="Cercar javamon de l'alumne..."
                                        prefix={<SearchOutlined />}
                                        value={searchJavamon}
                                        onChange={(e) => setSearchJavamon(e.target.value)}
                                        style={{ width: 280 }}
                                        allowClear
                                    />
                                </div>

                                <Table
                                    dataSource={filteredUserJavamons}
                                    rowKey="id_instance"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    columns={[
                                        {
                                            title: 'Sprite',
                                            key: 'img',
                                            width: 60,
                                            render: (_, r: any) => {
                                                const imgUrl = getExternalJavamonImage(r);
                                                return imgUrl ? (
                                                    <img src={imgUrl} alt={r.nom} style={{ width: 38, height: 38, objectFit: 'contain' }} />
                                                ) : (
                                                    <span style={{ fontSize: 24 }}>👾</span>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Javamon',
                                            dataIndex: 'nom',
                                            key: 'nom',
                                            render: (val, r: any) => {
                                                const rarityColors: Record<string, string> = {
                                                    normal: 'default',
                                                    raro: 'blue',
                                                    rare: 'blue',
                                                    epico: 'purple',
                                                    epic: 'purple',
                                                    legendario: 'gold',
                                                    legendary: 'gold'
                                                };
                                                const rColor = rarityColors[(r.rarity || '').toLowerCase()] || 'default';
                                                return (
                                                    <div>
                                                        <Text strong>{val}</Text>
                                                        {r.nom_origin && r.nom_origin !== val && (
                                                            <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 6 }}>
                                                                (Base: {r.nom_origin})
                                                            </span>
                                                        )}
                                                        <Tag color={rColor} style={{ marginLeft: 8 }}>
                                                            {r.rarity || 'Normal'}
                                                        </Tag>
                                                        {r.descr && <div style={{ fontSize: 11, color: '#595959' }}>{r.descr}</div>}
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Stats',
                                            key: 'stats',
                                            render: (_, r: any) => (
                                                <div style={{ fontSize: 11 }}>
                                                    <span>❤️ {r.vida ?? 0}</span> | <span>⚔️ {r.ataque ?? 0}</span> | <span>🛡️ {r.defensa ?? 0}</span> | <span>⚡ {r.velocidad ?? 0}</span>
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'Accions',
                                            key: 'actions',
                                            align: 'center',
                                            width: 100,
                                            render: (_, r: any) => (
                                                <Popconfirm
                                                    title="Treure Javamon?"
                                                    description="Aquesta instància de Javamon serà eliminada de l'alumne."
                                                    onConfirm={() => handleRemoveJavamon(r.id_instance)}
                                                    okText="Eliminar"
                                                    cancelText="Cancel·lar"
                                                    okButtonProps={{ danger: true }}
                                                >
                                                    <Button danger size="small" icon={<DeleteOutlined />}>
                                                        Treure
                                                    </Button>
                                                </Popconfirm>
                                            )
                                        }
                                    ]}
                                />
                            </div>
                        )
                    },
                    {
                        key: 'points',
                        label: <span><TrophyOutlined /> Punts i Recompenses ({totalPunts} pts)</span>,
                        children: (
                            <div>
                                {/* Formulario para Recompensa o Penalización */}
                                <Card size="small" style={{ marginBottom: 16, background: '#fffbe6', border: '1px solid #ffe58f' }}>
                                    <div style={{ fontWeight: 600, color: '#d48806', marginBottom: 10 }}>
                                        ⚖️ Atorgar Recompensa o Aplicar Penalització
                                    </div>
                                    <Row gutter={12} align="middle">
                                        <Col span={6}>
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                min={1}
                                                max={1000}
                                                value={pointsDelta}
                                                onChange={(val) => setPointsDelta(val || 1)}
                                                addonAfter="Punts"
                                            />
                                        </Col>
                                        <Col span={10}>
                                            <Input
                                                placeholder="Motiu (ex: Participació, Retard, Extra...)"
                                                value={pointsReason}
                                                onChange={(e) => setPointsReason(e.target.value)}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Space>
                                                <Button
                                                    type="primary"
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    icon={<CheckCircleOutlined />}
                                                    onClick={() => handleAdjustPoints(false)}
                                                    loading={loading}
                                                >
                                                    +
                                                </Button>
                                                <Button
                                                    danger
                                                    icon={<CloseCircleOutlined />}
                                                    onClick={() => handleAdjustPoints(true)}
                                                    loading={loading}
                                                >
                                                    -
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Historial de puntos */}
                                <Table
                                    dataSource={userPointsHistory}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    columns={[
                                        {
                                            title: 'Punts',
                                            dataIndex: 'punts',
                                            key: 'punts',
                                            width: 100,
                                            render: (val) => {
                                                const n = Number(val) || 0;
                                                return (
                                                    <Tag color={n >= 0 ? 'green' : 'red'} style={{ fontWeight: 700 }}>
                                                        {n >= 0 ? `+${n}` : n} pts
                                                    </Tag>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Tipus / Concepte',
                                            dataIndex: 'tipo',
                                            key: 'tipo',
                                            render: (val, r: any) => (
                                                <div>
                                                    <Text strong>{val || 'Punts'}</Text>
                                                    {r.descripcio && (
                                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.descripcio}</div>
                                                    )}
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'Accions',
                                            key: 'actions',
                                            align: 'center',
                                            width: 90,
                                            render: (_, r: any) => (
                                                <Popconfirm
                                                    title="Eliminar registre de punts?"
                                                    onConfirm={() => handleDeletePointRecord(r.id)}
                                                    okText="Eliminar"
                                                    cancelText="Cancel·lar"
                                                    okButtonProps={{ danger: true }}
                                                >
                                                    <Button danger size="small" type="text" icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            )
                                        }
                                    ]}
                                />
                            </div>
                        )
                    },
                    {
                        key: 'gasto',
                        label: <span><HistoryOutlined /> Historial de Gasto ({userSpentPointsHistory.length})</span>,
                        children: (
                            <div>
                                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        Registre detallat de compres a la botiga, consum de recursos i deduccions de punts.
                                    </Text>
                                    <Tag color="volcano" style={{ fontSize: 13, padding: '4px 10px', fontWeight: 700 }}>
                                        Total Gastat: -{totalGasto} pts
                                    </Tag>
                                </div>

                                <Table
                                    dataSource={userSpentPointsHistory}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 6 }}
                                    columns={[
                                        {
                                            title: 'Gasto / Us',
                                            dataIndex: 'punts',
                                            key: 'punts',
                                            width: 130,
                                            render: (val, r: any) => {
                                                if (r.ball === 'JAVABALL_LOG') {
                                                    return (
                                                        <Tag color={Number(r.capturat) === 1 ? 'green' : 'volcano'} style={{ fontWeight: 700, fontSize: 12 }}>
                                                            🎯 1 Javaball
                                                        </Tag>
                                                    );
                                                }
                                                const n = Math.abs(Number(val) || 0);
                                                return (
                                                    <Tag color="volcano" style={{ fontWeight: 700, fontSize: 12 }}>
                                                        -{n} pts
                                                    </Tag>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Concepte / Motiu',
                                            dataIndex: 'descripcio',
                                            key: 'descripcio',
                                            render: (desc, r: any) => (
                                                <div>
                                                    <Text strong>{desc || r.tipo || 'Despesa de punts'}</Text>
                                                    {r.tipo && r.tipo !== desc && (
                                                        <div>
                                                            <Tag color={r.ball === 'JAVABALL_LOG' ? (Number(r.capturat) === 1 ? 'success' : 'error') : 'orange'} style={{ fontSize: 11, marginTop: 2 }}>
                                                                {r.tipo}
                                                            </Tag>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'Accions',
                                            key: 'actions',
                                            align: 'center',
                                            width: 90,
                                            render: (_, r: any) => (
                                                <Popconfirm
                                                    title="Eliminar registre?"
                                                    description={r.ball === 'JAVABALL_LOG' ? "Eliminar aquest registre de llançament de Javaball." : "Eliminar aquest registre restaurarà el balanç associat."}
                                                    onConfirm={() => handleDeletePointRecord(r.id)}
                                                    okText="Eliminar"
                                                    cancelText="Cancel·lar"
                                                    okButtonProps={{ danger: true }}
                                                >
                                                    <Button danger size="small" type="text" icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            )
                                        }
                                    ]}
                                />
                            </div>
                        )
                    },
                    {
                        key: 'javaballs',
                        label: <span>🎯 Javaballs Llançades ({javaballLogs.length})</span>,
                        children: (
                            <div>
                                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        Registre cronològic de totes les Javaballs llançades per l'alumne des de la Web o des del Joc per intentar capturar un Javamon.
                                    </Text>
                                    <Space size={8}>
                                        <Tag color="green" style={{ fontWeight: 600 }}>
                                            Capturats: {javaballLogs.filter((l: any) => Number(l.capturat) === 1).length}
                                        </Tag>
                                        <Tag color="volcano" style={{ fontWeight: 600 }}>
                                            Escapats: {javaballLogs.filter((l: any) => Number(l.capturat) !== 1).length}
                                        </Tag>
                                    </Space>
                                </div>

                                <Table
                                    dataSource={javaballLogs}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 6 }}
                                    columns={[
                                        {
                                            title: 'Data i Hora',
                                            dataIndex: 'moment',
                                            key: 'moment',
                                            width: 155,
                                            render: (v: string) => v ? new Date(v).toLocaleString() : '-'
                                        },
                                        {
                                            title: 'Javaball Llançada',
                                            dataIndex: 'ball_nom',
                                            key: 'ball_nom',
                                            render: (val: string, r: any) => {
                                                const maxBase = r.max_roll ? Number(r.max_roll) : (r.origen === 'GAME' ? 150 : 200);
                                                const pct = r.ball_punts ? Math.max(0, Math.min(100, Math.round((Number(r.ball_punts) / maxBase) * 100))) : null;
                                                return (
                                                    <div>
                                                        <Tag color="geekblue" style={{ fontWeight: 700 }}>
                                                            🎯 {val || 'JAVAball'}
                                                        </Tag>
                                                        {pct !== null && (
                                                            <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                                ({r.ball_punts}/{maxBase} pts · {pct}%)
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Tirada (Número)',
                                            key: 'roll',
                                            align: 'center',
                                            width: 140,
                                            render: (_: any, r: any) => {
                                                const maxBase = r.max_roll ? Number(r.max_roll) : (r.origen === 'GAME' ? 150 : 200);
                                                if (r.roll == null) {
                                                    return <span style={{ color: '#8c8c8c', fontSize: 12 }}>-</span>;
                                                }
                                                const success = Number(r.capturat) === 1;
                                                return (
                                                    <div>
                                                        <Tag color={success ? 'green' : 'volcano'} style={{ fontWeight: 700 }}>
                                                            🎲 {r.roll} / {maxBase}
                                                        </Tag>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Javamon Objectiu',
                                            dataIndex: 'javamon_nom',
                                            key: 'javamon_nom',
                                            render: (val: string, r: any) => (
                                                <Text strong>
                                                    {val || 'Javamon'}
                                                    {r.id_javamon ? <span style={{ color: '#8c8c8c', fontWeight: 400, marginLeft: 4 }}>#{r.id_javamon}</span> : null}
                                                </Text>
                                            )
                                        },
                                        {
                                            title: 'Origen',
                                            dataIndex: 'origen',
                                            key: 'origen',
                                            width: 100,
                                            align: 'center',
                                            render: (val: string) => (
                                                <Tag color={val === 'GAME' ? 'purple' : 'cyan'}>
                                                    {val === 'GAME' ? '🎮 JOC' : '🌐 WEB'}
                                                </Tag>
                                            )
                                        },
                                        {
                                            title: 'Resultat',
                                            dataIndex: 'capturat',
                                            key: 'capturat',
                                            width: 120,
                                            align: 'center',
                                            render: (val: any) => Number(val) === 1 ? (
                                                <Tag color="green" style={{ fontWeight: 700 }}>✅ CAPTURAT</Tag>
                                            ) : (
                                                <Tag color="red" style={{ fontWeight: 700 }}>❌ ESCAPAT</Tag>
                                            )
                                        }
                                    ]}
                                />
                            </div>
                        )
                    }
                ]}
            />
        </Modal>
    );
};

export default UserInventoryModal;
